<script setup lang="ts">
import {
    BlockArrayItems,
    BlockBaseElement,
    BlockDataValue,
} from "@/views/blocks/PageBlock.interface";
import { ButtonSecondaryIconSm, ButtonTertiaryIconXs } from "@easy/ui/buttons";
import { CardToggleAlt } from "@easy/ui/cards";
import { AddIcon, RecycleBinIcon } from "@easy/ui/icons";
import { BlockDefinition } from "@/apps/page/services/blocks/block.interfaces";
import type { PageSection } from "@/apps/page/page.interfaces";
import { Component } from "vue";
import { randomId } from "@/shared/utils/helpers";
import { FormInput, FormRichText, FormSelect, FormToggleSm, FormTextArea } from "@easy/ui/forms";
import BlockImage from "./BlockImage.vue";
import LinkField from "./components/LinkField.vue";
import GroupMedia from "./components/GroupMedia.vue";

const { groupedField, section, groupIcon } = defineProps<{
    groupedField: BlockDefinition<BlockBaseElement<BlockDataValue>>;
    section: PageSection;
    groupIcon: Component;
}>();

function addItem() {
    (section.dataValues[groupedField.value].data as BlockArrayItems<unknown>).items.push({
        id: randomId(),
        ...(groupedField.newItem ?? groupedField.defaultData),
    });
}

function removeItem(index: number) {
    (section.dataValues[groupedField.value].data as BlockArrayItems<unknown>).items.splice(
        index,
        1
    );
}
</script>

<template>
    <div class="flex justify-end">
        <ButtonSecondaryIconSm @click="addItem" class="flex gap-2">
            <AddIcon />
        </ButtonSecondaryIconSm>
    </div>
    <CardToggleAlt
        v-for="(item, index) in (
            section.dataValues[groupedField.value].data as BlockArrayItems<any>
        ).items"
        :key="groupedField.value + '_' + item.id"
        :data-field="groupedField.value + '_' + item.id"
        :title="groupedField.label + ' ' + (index + 1)"
        :icon="groupIcon"
    >
        <template #default>
            <div class="rounded-md space-y-3 pb-3">
                <div
                    v-for="field in groupedField.fields.filter((field) => field.type !== 'media')"
                    :key="field.value"
                >
                    <FormInput
                        :id="field.value"
                        v-if="field.type === 'text'"
                        :title="field.label"
                        v-model="item[field.value]"
                    />

                    <FormSelect
                        v-if="field.type === 'select'"
                        v-model="item[field.value]"
                        :label="field.label"
                        :options="field.options"
                        :value="item[field.value]"
                    />

                    <FormTextArea
                        :id="field.value"
                        v-if="field.type === 'text'"
                        :title="field.label"
                        v-model="item[field.value]"
                    />

                    <FormRichText
                        :id="field.value"
                        v-if="field.type === 'html'"
                        :title="field.label"
                        v-model="item[field.value]"
                        :rows="field.rows"
                    />

                    <BlockImage
                        :id="field.value"
                        v-if="field.type === 'image'"
                        :title="field.label"
                        v-model="item[field.value]"
                    />

                    <FormToggleSm
                        :id="field.value"
                        v-if="field.type === 'switch'"
                        v-model="item[field.value]"
                        :title="field.label"
                    />

                    <LinkField :field="field" :item="item" v-if="field.type === 'link'" />
                </div>
                <div
                    v-if="groupedField.fields.filter((field) => field.type === 'media').length > 0"
                >
                    <GroupMedia
                        :fields="groupedField.fields.filter((field) => field.type === 'media')"
                        :group-data="item"
                    />
                </div>
            </div>
        </template>

        <template #actions>
            <ButtonTertiaryIconXs @click="removeItem(index)">
                <RecycleBinIcon />
            </ButtonTertiaryIconXs>
        </template>
    </CardToggleAlt>
</template>
